// choiceEdits — a question's answer edits, for the editor host.
//
// Split out of `EditorHost.tsx` (hotspot decomposition): the handlers that
// change a question's answers (add, remove, move one, mark the right one,
// the answer mode) and end the question with a line below it. Each is the
// same optimistic apply + queued save as every structural edit, through the
// host's runner; this module owns no state. Built each render from the
// host's current tree.

export { mkChoiceEdits };

import * as Limits from "@bilda/core/limits";
import { Result } from "@bilda/utils";
import type { AppPorts } from "../../app/ports.js";
import {
    anchorForMove,
    applyEdit,
    markChoiceCorrect,
    moveChoice,
    unmarkChoiceCorrect,
} from "../Editor/edit.js";
import type {
    Choice,
    Element,
    ElementId,
    KeptLine,
    QuestionEnd,
} from "../Editor/types.js";
import { affectedCorrectness, atPaths } from "./edits.js";
import type { FieldSave } from "./saveQueue.js";
import { sendOps } from "./sendOps.js";
import { correctOp } from "./undoSteps.js";
import type { StructuralRun } from "./useSaveQueue.js";

type ChoiceEditDeps = {
    readonly ports: AppPorts;
    // The tree as shown (null: nothing loaded).
    readonly elements: readonly Element[] | null;
    // Whether an edit to the element can be saved (it has a path).
    readonly editable: (id: ElementId) => boolean;
    // The host's optimistic-persist runner (show `next`, queue `persist`).
    readonly runStructuralEdit: (
        next: readonly Element[],
        persist: StructuralRun["persist"],
        mints?: ElementId,
        base?: readonly Element[] | null,
    ) => void;
    // A fresh id (the host mints it, the impure edge).
    readonly mintId: () => ElementId;
    // Keep a field's text: the tree as now shown (undefined: not editable).
    readonly commitField: (field: FieldSave) => readonly Element[] | undefined;
    // The caret goes into this element's text when it mounts.
    readonly focus: (id: ElementId) => void;
};

const mkChoiceEdits = ({
    ports,
    elements,
    editable,
    runStructuralEdit,
    mintId,
    commitField,
    focus,
}: ChoiceEditDeps) => {
    // Persist a correctness change: diff the pre/post trees for every choice
    // whose `correct` flag moved, then PATCH each through the keyed-merge
    // primitive (`patch_element_attrs`, `{ correct }`) so the write never
    // clobbers the choice's `feedback` — the whole point of the merge verb. On a
    // single-answer MCQ the fold cleared the prior key, so the diff yields two
    // affected choices (new key true + old key false); each gets its own patch,
    // in order. Every affected choice must be path-editable; if any is not the
    // change is a no-op (content that is not path-editable).
    const persistChoiceCorrectness = (next: readonly Element[]) => {
        if (elements === null) return;
        const affected = affectedCorrectness(elements, next);
        if (affected.length === 0) return; // no-op fold (unknown id / no change)
        const ids = affected.map((a) => a.choiceId);
        if (!ids.every(editable)) return; // not path-editable
        runStructuralEdit(next, sendOps(ports, affected.map(correctOp)));
    };

    // Mark / unmark a choice as the correct answer — TWO intents, two commands
    // (never a setCorrect(boolean) flag). Each resolves the owning MCQ by choice
    // id through the pure fold (which enforces "exactly one key" on single-answer
    // items), then persists the diff. A single-answer radio only ever reaches
    // the mark path; unmark is wired for the deferred multiple-answer control.
    const onMarkChoiceCorrect = (choiceId: ElementId) => {
        if (elements === null) return;
        persistChoiceCorrectness(markChoiceCorrect(elements, choiceId));
    };
    const onUnmarkChoiceCorrect = (choiceId: ElementId) => {
        if (elements === null) return;
        persistChoiceCorrectness(unmarkChoiceCorrect(elements, choiceId));
    };

    // Enter in the last answer's why line (questionKeys.ts): the line's text
    // is kept, then a fresh empty answer follows it. The two go as one chain
    // (the add is built on the tree the text left), through
    // `insert_child_element`, whose returned path makes the new answer
    // editable. At the answers ceiling (CL-25) nothing is added. The MCQ's
    // own path must resolve; pqbl course-object content has none ⇒ no-op.
    const onAddChoice = (mcqId: ElementId, after: KeptLine) => {
        const base = commitField(
            after.type === "stem"
                ? { type: "text", id: mcqId, text: after.text }
                : {
                      type: "feedback",
                      id: after.choiceId,
                      feedback: after.feedback,
                  },
        );
        if (base === undefined || !editable(mcqId)) return;
        const mcq = base.find((e) => e.id === mcqId);
        if (
            mcq?.type !== "mcq" ||
            mcq.choices.length >= Limits.QUESTION_ANSWERS_MAX
        )
            return;
        const choiceId: ElementId = mintId();
        const choice: Choice = { id: choiceId, text: "", correct: false };
        runStructuralEdit(
            applyEdit(base)({ type: "add-choice", mcqId, choice }),
            atPaths([mcqId], (materialId, hash, pathOf) =>
                ports.addChoice(materialId, pathOf(mcqId), choice, hash),
            ),
            choiceId,
            base,
        );
    };

    // Enter on an empty answer ends the question: an ordinary empty line
    // follows it, the caret in it. The answer goes with it when it may (not
    // the only or the right one), else its text is kept. Sent as one chain:
    // the line first (so a refused insert changes nothing), then the remove.
    const onEndQuestion = (mcqId: ElementId, end: QuestionEnd) => {
        const base =
            end.type === "keep-answer"
                ? commitField({
                      type: "text",
                      id: end.choiceId,
                      text: end.text,
                  })
                : elements;
        if (base === null || base === undefined || !editable(mcqId)) return;
        const line: Element = { type: "prose", id: mintId(), markdown: "" };
        const shown =
            end.type === "drop-answer"
                ? applyEdit(base)({
                      type: "remove-choice",
                      mcqId,
                      choiceId: end.choiceId,
                  })
                : base;
        const insert = atPaths([mcqId], (materialId, hash, pathOf) =>
            ports.insertBlockAfter(materialId, pathOf(mcqId), line, hash),
        );
        const remove = atPaths([end.choiceId], (materialId, hash, pathOf) =>
            ports.removeElement(materialId, pathOf(end.choiceId), hash),
        );
        runStructuralEdit(
            applyEdit(shown)({
                type: "insert-element",
                element: line,
                index: shown.findIndex((e) => e.id === mcqId) + 1,
            }),
            end.type === "keep-answer"
                ? insert
                : async (materialId, hash, paths) => {
                      const inserted = await insert(materialId, hash, paths);
                      if (Result.isErr(inserted)) return inserted;
                      const removed = await remove(
                          materialId,
                          inserted.value.contentHash,
                          paths,
                      );
                      return Result.isErr(removed)
                          ? removed
                          : Result.ok({
                                contentHash: removed.value.contentHash,
                                path: inserted.value.path,
                            });
                  },
            line.id,
            base,
        );
        focus(line.id);
    };

    // Remove one choice from an MCQ through `remove_element` (a choice IS an
    // element at `[...mcqPath, choiceId]`). Unresolvable path ⇒ no-op.
    const onRemoveChoice = (mcqId: ElementId, choiceId: ElementId) => {
        if (elements === null || !editable(choiceId)) return;
        runStructuralEdit(
            applyEdit(elements)({
                type: "remove-choice",
                mcqId,
                choiceId,
            }),
            atPaths([choiceId], (materialId, hash, pathOf) =>
                ports.removeElement(materialId, pathOf(choiceId), hash),
            ),
        );
    };

    // Reorder one choice within its MCQ to `toIndex` — the choice-list twin of
    // onMoveElement: translate the index to a sibling anchor among the MCQ's
    // choices, then persist through `move_element`.
    const onMoveChoice = (
        mcqId: ElementId,
        choiceId: ElementId,
        toIndex: number,
    ) => {
        if (elements === null) return;
        const mcq = elements.find((e) => e.id === mcqId);
        if (mcq === undefined || mcq.type !== "mcq") return;
        const anchor = anchorForMove(mcq.choices, choiceId, toIndex);
        if (anchor === undefined) return;
        if (!editable(choiceId) || !editable(anchor.anchorId)) return;
        runStructuralEdit(
            moveChoice(elements, choiceId, toIndex),
            atPaths([choiceId, anchor.anchorId], (materialId, hash, pathOf) =>
                ports.moveElement(
                    materialId,
                    pathOf(choiceId),
                    pathOf(anchor.anchorId),
                    anchor.position,
                    hash,
                ),
            ),
        );
    };

    // Widen an MCQ to multiple-answer: one merge-safe answerMode patch, no
    // correctness change. Unresolvable path ⇒ no-op.
    const onAllowMultipleAnswers = (mcqId: ElementId) => {
        if (elements === null || !editable(mcqId)) return;
        runStructuralEdit(
            applyEdit(elements)({ type: "allow-multiple-answers", mcqId }),
            atPaths([mcqId], (materialId, hash, pathOf) =>
                ports.patchElementAttrs(
                    materialId,
                    pathOf(mcqId),
                    { answerMode: "multiple" },
                    hash,
                ),
            ),
        );
    };

    // Narrow an MCQ to single-answer: the fold sets the mode AND clears any extra
    // key (keeping the first). Persist BOTH — the answerMode attr on the MCQ, then
    // each affected choice's cleared `correct` — in order, as one save. The MCQ
    // path and every affected choice path must resolve, else no-op.
    const onAllowSingleAnswer = (mcqId: ElementId) => {
        if (elements === null) return;
        const next = applyEdit(elements)({
            type: "allow-single-answer",
            mcqId,
        });
        const cleared = affectedCorrectness(elements, next);
        const ids = [mcqId, ...cleared.map((a) => a.choiceId)];
        if (!ids.every(editable)) return;
        runStructuralEdit(
            next,
            sendOps(ports, [
                { type: "attrs", id: mcqId, attrs: { answerMode: "single" } },
                ...cleared.map(correctOp),
            ]),
        );
    };

    return {
        onMarkChoiceCorrect,
        onUnmarkChoiceCorrect,
        onAddChoice,
        onEndQuestion,
        onRemoveChoice,
        onMoveChoice,
        onAllowMultipleAnswers,
        onAllowSingleAnswer,
    };
};
